import { ReactNode, memo, useEffect, useMemo, useRef, useState } from 'react';
import { alpha, SxProps, Theme, useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { CheckOutlined, CopyOutlined, MinusOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import { RADIUS, REDUCED_MOTION } from 'components/ui/tokens';
import { focusRingSx } from 'components/ui/styleKit';
import mt4Logo from 'assets/images/icons/mt4_logo.png';
import mt5Logo from 'assets/images/icons/mt5_logo.png';
import { inputsCopy } from './eaConnectCopy';
import { InputPagerPage } from './inputPages';

// ==============================|| TOKENS ||============================== //

// True monospace for terminal/log/price grids. IBM Plex Mono rather than a
// second mono face: it is already loaded app-wide, so EA Connect matches the
// rest of the UI and costs no extra font download.
export const MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

// Inter tabular figures for hero numbers — column-aligned without the typewriter look.
export const tnum = { fontFeatureSettings: '"tnum"', fontVariantNumeric: 'tabular-nums' as const };

// Card elevation shared with the app's StatsCard (soft shadow in light, hairline in dark).
export const tileShell = (theme: Theme) => ({
  borderRadius: '10px',
  backgroundColor: theme.palette.background.paper,
  border: theme.palette.mode === 'dark' ? `1px solid ${alpha('#fff', 0.1)}` : `1px solid ${theme.palette.border.main}`,
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0px 1px 2px 0px rgba(21, 21, 21, 0.06)'
});

// ── Command band: the fixed-dark console stage shared by Fleet and the
// instance detail header. The band is dark in BOTH themes, so every ink
// inside it must be pinned — theme-tracking greys/mains are banned here
// (they collapse to ~2:1 in one of the modes). Components that can sit on
// the band take ink="onDark" to switch to these pinned shades.
export const BAND_INK = {
  hi: '#EDF2FA',
  mid: 'rgba(214,224,238,0.62)',
  low: 'rgba(214,224,238,0.45)',
  up: '#4DD48A',
  down: '#FF7A7A',
  teal: '#5AC8BC',
  hairline: 'rgba(148,170,196,0.14)'
};

// The glowing primary hairline that closes a dark stage at the bottom (command band, landing hero).
export const glowHairline = (theme: Theme) => ({
  content: '""',
  position: 'absolute' as const,
  left: 0,
  right: 0,
  bottom: 0,
  height: '1px',
  background: `linear-gradient(90deg, transparent 2%, ${alpha(theme.palette.primary.main, 0.55)} 30%, ${alpha(
    theme.palette.primary.light,
    0.75
  )} 50%, ${alpha(theme.palette.primary.main, 0.55)} 70%, transparent 98%)`
});

export const consoleBand = (theme: Theme) => ({
  position: 'relative' as const,
  overflow: 'hidden',
  borderRadius: '14px',
  color: BAND_INK.hi,
  bgcolor: '#0C1226',
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 12px 40px -18px rgba(11,15,32,0.55)',
  backgroundImage: [
    `radial-gradient(560px 240px at 12% -40px, ${alpha(theme.palette.primary.main, 0.22)}, transparent 65%)`,
    'linear-gradient(rgba(148,170,196,0.10) 1px, transparent 1px)',
    'linear-gradient(90deg, rgba(148,170,196,0.10) 1px, transparent 1px)',
    'linear-gradient(180deg, #0A0F20 0%, #0C1226 55%, #0E1B2E 100%)'
  ].join(','),
  backgroundSize: 'auto, 44px 44px, 44px 44px, auto',
  backgroundPosition: '0 0, -1px -1px, -1px -1px, 0 0',
  '&::after': glowHairline(theme)
});

// One stat on a command band: caps label, value, mono sub-line. `first` drops the desktop divider.
export const BandStatCell = ({
  label,
  body,
  sub,
  subColor,
  first
}: {
  label: string;
  body: ReactNode;
  sub: ReactNode;
  subColor?: string;
  first: boolean;
}) => (
  <Stack
    spacing={0.25}
    sx={{
      py: 2,
      pl: { xs: 0, md: first ? 0 : 3 },
      pr: { xs: 2, md: 3 },
      minWidth: 0,
      borderLeft: { xs: 'none', md: first ? 'none' : `1px solid ${BAND_INK.hairline}` }
    }}
  >
    <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: BAND_INK.low }}>
      {label}
    </Typography>
    <Box>{body}</Box>
    <Typography sx={{ fontFamily: MONO, fontSize: '0.625rem', color: subColor || BAND_INK.low, ...tnum }}>{sub}</Typography>
  </Stack>
);

// Dark-safe ghost button for the band (theme outlined buttons vanish on it).
export const bandGhostButtonSx = {
  color: BAND_INK.hi,
  borderColor: 'rgba(255,255,255,0.25)',
  // The theme's outlined hover switches the label to dark primary, which vanishes on the band.
  '&:hover': { color: BAND_INK.hi, borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
  '&.Mui-disabled': { color: 'rgba(214,224,238,0.35)', borderColor: 'rgba(255,255,255,0.12)' }
};

// Staggered entrance — apply per element with an index: sx={{ ...reveal(i) }}.
// Keyframes ride along in the sx so no global stylesheet is needed.
export const reveal = (i = 0) => ({
  opacity: 0,
  animation: 'eaFadeUp 480ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards',
  animationDelay: `${i * 70}ms`,
  '@keyframes eaFadeUp': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  // Base opacity is 0, so stopping the animation alone would leave the
  // element invisible — restore full opacity under reduced motion.
  [REDUCED_MOTION]: { animation: 'none', opacity: 1 }
});

// Left inset bar on an input whose value differs from the EA's default.
export const changedAccentSx = (theme: Theme) => ({
  position: 'relative' as const,
  '&::before': {
    content: '""',
    position: 'absolute' as const,
    left: -10,
    top: 6,
    bottom: 6,
    width: 2,
    borderRadius: 1,
    bgcolor: theme.palette.primary.main
  }
});

// The theme greys a disabled button's label out almost completely; this keeps it readable (e.g. a countdown).
export const readableDisabledSx = { '&.Mui-disabled': { color: 'text.secondary', borderColor: 'divider' } };

// Hover lift for interactive tiles/cards — pairs with tileShell.
export const hoverLift = (theme: Theme) => ({
  transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.35),
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 8px 24px ${alpha('#000', 0.45)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.12)}`
        : `0 10px 28px ${alpha('#151515', 0.1)}`
  }
});

// Shimmer sweep for loading skeleton surfaces.
export const shimmer = (theme: Theme) => ({
  position: 'relative' as const,
  overflow: 'hidden' as const,
  backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.05),
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    transform: 'translateX(-100%)',
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.text.primary, 0.07)}, transparent)`,
    animation: 'eaShimmer 1.6s ease-in-out infinite'
  },
  '@keyframes eaShimmer': { to: { transform: 'translateX(100%)' } },
  [REDUCED_MOTION]: { '&::after': { animation: 'none' } }
});

// Dark "terminal" panel surface. Light mode: deep navy on white (high contrast).
// Dark mode: slightly elevated above the page (#0B0F20) so it still reads as a panel.
export const terminalSurface = (theme: Theme) => ({
  bg: theme.palette.mode === 'dark' ? '#161B2E' : '#0B0F20',
  border: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.07)'
});

// The dark terminal card behind the extraction console and the event feeds.
export const terminalShell = (theme: Theme) => {
  const surf = terminalSurface(theme);
  return { borderRadius: '10px', overflow: 'hidden', bgcolor: surf.bg, border: `1px solid ${surf.border}` };
};

// A terminal card standing on the page (the feeds): column layout, lifted in light mode.
export const terminalFeedShell = (theme: Theme) => ({
  ...terminalShell(theme),
  display: 'flex',
  flexDirection: 'column' as const,
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(0,0,0,0.18)'
});

// The title row on top of a terminal card.
export const terminalHeaderSx = { px: 2.5, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' };

// ==============================|| EQUITY AREA CHART (Analysis-Hub style, reusable) ||============================== //

export interface EquityPoint {
  // Epoch ms. The x-axis is a real time axis, not a list of categories: in
  // category mode Apex builds a <text>+<title>+<tspan> triplet for EVERY
  // sample — 24h of per-minute data meant 3027 SVG nodes to show eight
  // readable labels. On a datetime axis it derives ticks from the range.
  ts: number;
  label: string;
  full: string;
  // null = a telemetry gap (the terminal was disconnected). Apex breaks the
  // line on nulls, so an outage renders as a visible gap instead of being
  // silently stitched over — or worse, plotted as a cliff to $0.
  equity: number | null;
  trades?: number;
}

// This chart renders ~3000 SVG nodes — roughly four fifths of the bot detail
// page. Rebuilding `options`/`series` on every parent render handed Apex fresh
// object identities every time, so the chart re-initialised (and, with
// animations on, re-animated) on every one-second uptime tick. Memoised on the
// data it actually draws, plus React.memo on the props, it now rebuilds only
// when the equity series changes.
export const EquityAreaChart = memo(({ points, height = 232 }: { points: EquityPoint[]; height?: number }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const paper = theme.palette.background.paper;

  const model = useMemo(() => {
    const values = points.map((p) => p.equity);
    // Gap markers are null; every scalar below must ignore them.
    const real = values.filter((v): v is number => v != null);
    return {
      values,
      seriesData: points.map((p) => ({ x: p.ts, y: p.equity })),
      real,
      hasGaps: real.length !== values.length,
      up: real[real.length - 1] >= real[0],
      actualMin: Math.min(...real),
      actualMax: Math.max(...real)
    };
  }, [points]);

  const { seriesData, real, hasGaps, up, actualMin, actualMax } = model;
  const color = up ? '#00897B' : '#FF6262';
  const gradientTo = up ? 'rgba(0, 137, 123, 0.05)' : 'rgba(255, 98, 98, 0.05)';

  const options: any = useMemo(
    () => ({
      chart: {
        type: 'area',
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { easing: 'easeout', speed: 500 },
        fontFamily: 'Inter, sans-serif'
      },
      dataLabels: { enabled: false },
      // 'smooth' is an Apex landmine when the series contains nulls (gap
      // markers): the bezier path collapses into a scribble and repaints
      // differently on every update. Straight segments render breaks correctly.
      stroke: { width: 2, curve: hasGaps ? 'straight' : 'smooth', colors: [color], lineCap: 'round' },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.3,
          gradientToColors: [gradientTo],
          inverseColors: false,
          opacityFrom: 0.4,
          opacityTo: 0.05,
          stops: [0, 100]
        },
        colors: [color]
      },
      markers: { size: 0, colors: [color], strokeWidth: 2, strokeColors: paper, hover: { size: 6, sizeOffset: 2 } },
      grid: { borderColor: isDark ? '#2d3748' : '#e7e9ed', strokeDashArray: 0, padding: { left: 10, top: 0, bottom: 0 } },
      xaxis: {
        type: 'datetime',
        tickAmount: 6,
        labels: {
          style: { fontSize: '10px', fontFamily: 'Inter, sans-serif', colors: isDark ? '#a0aec0' : '#6b7580', fontWeight: 400 },
          hideOverlappingLabels: true,
          datetimeUTC: false,
          format: 'HH:mm'
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: { show: true, stroke: { color: isDark ? '#2d3748' : '#e0e3e8', width: 1, dashArray: 4 } },
        tooltip: { enabled: false }
      },
      yaxis: {
        min: Math.max(0, actualMin * 0.97),
        max: actualMax * 1.03,
        tickAmount: 5,
        labels: {
          style: { fontSize: '10px', fontFamily: 'Inter, sans-serif', colors: isDark ? '#a0aec0' : '#6b7580' },
          formatter: (val: number) => (Math.abs(val) >= 1000 ? `${(val / 1000).toFixed(1)}K` : val.toFixed(0))
        }
      },
      tooltip: {
        theme: 'dark',
        custom: ({ dataPointIndex }: any) => {
          const point = points[dataPointIndex];
          const value = point ? point.equity : null;
          // Hovering a gap marker: value is null and toLocaleString would throw.
          if (value == null || !point) {
            return `
          <div style="background:${
            isDark ? '#161B2E' : '#0b0f20'
          };border:1px solid rgba(255,255,255,0.12);padding:8px 10px;border-radius:6px;color:#fff;font-family:Inter,sans-serif;">
            <div style="font-size:10px;opacity:0.6;">telemetry gap</div>
            <div style="font-size:11px;margin-top:2px;">no data — terminal was offline</div>
          </div>`;
          }
          const tradesRow =
            point.trades != null
              ? `<div style="display:flex;align-items:baseline;gap:4px;margin-top:2px;font-size:11px;line-height:1.3;letter-spacing:-0.2px;"><span style="opacity:0.7;">Trades</span><span style="font-weight:700;">${point.trades}</span></div>`
              : '';
          return `
          <div style="background:${
            isDark ? '#161B2E' : '#0b0f20'
          };border:1px solid rgba(255,255,255,0.12);padding:8px 10px;border-radius:6px;color:#fff;font-family:Inter,sans-serif;">
            <div style="font-size:10px;font-weight:400;line-height:1.3;letter-spacing:-0.2px;opacity:0.6;">${point.full}</div>
            <div style="display:flex;align-items:baseline;gap:4px;margin-top:4px;font-size:11px;line-height:1.3;letter-spacing:-0.2px;">
              <span style="opacity:0.7;">Equity</span><span style="font-weight:700;">$${value.toLocaleString()}</span>
            </div>
            ${tradesRow}
          </div>`;
        }
      }
    }),
    [points, isDark, paper, color, gradientTo, hasGaps, actualMin, actualMax]
  );

  // A fresh array literal here would defeat the memo above — Apex diffs series
  // by identity before it diffs by content.
  const series = useMemo(() => [{ name: 'Equity', data: seriesData }], [seriesData]);

  // An empty series feeds Infinity into the y-axis bounds and Apex never
  // recovers; the caller shows its own empty state. Checked after the hooks so
  // hook order stays stable across renders.
  if (real.length === 0) return null;

  return <ReactApexChart type="area" height={height} options={options} series={series} />;
});
EquityAreaChart.displayName = 'EquityAreaChart';

// ==============================|| GROUP HEADER ||============================== //

interface GroupHeaderProps {
  icon: ReactNode;
  title: string;
  caption?: string;
  tint?: string; // palette key resolver result, e.g. theme.palette.primary.main
  action?: ReactNode;
}

export const GroupHeader = ({ icon, title, caption, tint, action }: GroupHeaderProps) => {
  const theme = useTheme();
  const color = tint || theme.palette.primary.main;
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${alpha(color, 0.18)} 0%, ${alpha(color, 0.06)} 100%)`,
            border: `1px solid ${alpha(color, 0.22)}`,
            color,
            fontSize: 16
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3 }}>{title}</Typography>
          {caption && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {caption}
            </Typography>
          )}
        </Box>
      </Stack>
      {action}
    </Stack>
  );
};

// ==============================|| INPUT PAGER ||============================== //

// Pills over the EA input pages (deploy Configure + Settings dialog); a badge counts edits per page. Hidden for one page.
export const InputPager = ({
  pages,
  value,
  onChange,
  disabled,
  sx
}: {
  pages: InputPagerPage[];
  value: number;
  onChange: (page: number) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}) => {
  const theme = useTheme();
  if (pages.length < 2) return null;
  return (
    <Box sx={sx}>
      <Tabs
        value={value}
        onChange={(_, v: number) => onChange(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label={inputsCopy.pagerLabel}
        sx={{
          minHeight: 30,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-flexContainer': { gap: 0.5 },
          // MUI keeps a disabled scroll arrow as an invisible 40px block; collapse it so the pills sit flush.
          '& .MuiTabs-scrollButtons': { width: 28, transition: 'width 160ms ease', [REDUCED_MOTION]: { transition: 'none' } },
          '& .MuiTabs-scrollButtons.Mui-disabled': { width: 0 },
          '& .MuiTab-root': {
            minHeight: 30,
            minWidth: 0,
            px: 1.5,
            py: 0.5,
            borderRadius: RADIUS.pill,
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary'
          },
          '& .MuiTab-root.Mui-selected': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) }
        }}
      >
        {pages.map((p, i) => (
          <Tab
            key={i}
            value={i}
            disabled={disabled}
            label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <span>{p.label}</span>
                {p.changed > 0 && (
                  <Box
                    component="span"
                    title={inputsCopy.changedHint(p.changed)}
                    sx={{
                      minWidth: 16,
                      height: 16,
                      px: 0.5,
                      borderRadius: 8,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.625rem',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      ...tnum
                    }}
                  >
                    {p.changed}
                  </Box>
                )}
              </Stack>
            }
          />
        ))}
      </Tabs>
    </Box>
  );
};

// ==============================|| NUMERIC STEPPER ||============================== //

interface StepperProps {
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  step?: number;
  min?: number;
  width?: number;
}

export const NumericStepper = ({ value, onChange, prefix = '', step = 1, min = 0, width = 132 }: StepperProps) => {
  const theme = useTheme();
  const dec = () => onChange(Math.max(min, value - step));
  const inc = () => onChange(value + step);
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        width,
        border: `1px solid ${theme.palette.border.main}`,
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette.background.default, 0.6),
        px: 0.5
      }}
    >
      <IconButton size="small" onClick={dec} sx={{ color: 'text.secondary' }}>
        <MinusOutlined style={{ fontSize: 11 }} />
      </IconButton>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, ...tnum }}>
        {prefix}
        {value.toLocaleString()}
      </Typography>
      <IconButton size="small" onClick={inc} sx={{ color: 'text.secondary' }}>
        <PlusOutlined style={{ fontSize: 11 }} />
      </IconButton>
    </Stack>
  );
};

// ==============================|| STATUS DOT ||============================== //

export const STATUS_META: Record<string, { color: string; label: string }> = {
  running: { color: '#00897b', label: 'Running' },
  starting: { color: '#e67e22', label: 'Spinning up' },
  // Terminal alive, broker link down — the agent reconnects on its own.
  degraded: { color: '#e67e22', label: 'Reconnecting' },
  // A settings version is being applied: the terminal restarts to reload the
  // inputs, so the bot is briefly not trading and lifecycle actions are held.
  applying: { color: '#e67e22', label: 'Applying settings' },
  stopped: { color: '#9ca3af', label: 'Stopped' },
  error: { color: '#d13c31', label: 'Error' },
  // Non-lifecycle vocabulary for StatusChip call sites that are NOT instance
  // states (instance statuses only ever map to the four above, so these keys
  // cannot leak into bot rows).
  atCapacity: { color: '#e67e22', label: 'At capacity' },
  ready: { color: '#00897b', label: 'Ready' },
  extracting: { color: '#e67e22', label: 'Extracting' }
};

// StatusChip promotes the status color to label text on regular surfaces, so
// it must track the ACTIVE theme palette (presets change success/warning/
// error) and use mode-aware shades for contrast.
const STATUS_PALETTE: Record<string, 'success' | 'warning' | 'error'> = {
  running: 'success',
  starting: 'warning',
  degraded: 'warning',
  applying: 'warning',
  error: 'error',
  atCapacity: 'warning',
  ready: 'success',
  extracting: 'warning'
};

// ==============================|| CONFIRM DIALOG ||============================== //

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  // When set, the confirm button stays disabled until the user types this
  // exact string — for irreversible actions (bot delete).
  challenge?: string;
  // Extra detail under the body (e.g. the list of changed settings).
  children?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
}

export const ConfirmDialog = ({
  open,
  title,
  body,
  confirmLabel = 'Delete',
  challenge,
  children,
  onConfirm,
  onClose
}: ConfirmDialogProps) => {
  const theme = useTheme();
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);
  const blocked = !!challenge && typed !== challenge;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      // Above the settings Drawer (zIndex.modal + 10, and its backdrop carries
      // a blur): at the default modal depth this dialog paints BELOW that
      // backdrop-filter and is rendered blurry until a click promotes it to its
      // own compositing layer. A confirm prompt is always the topmost surface —
      // still under snackbars (1400) and tooltips (1500), which is correct.
      sx={{ zIndex: theme.zIndex.modal + 20 }}
      PaperProps={{ sx: { borderRadius: 2.5, p: 1 } }}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(3px)' } } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary">
          {body}
        </Typography>
        {children}
        {challenge && (
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder={challenge}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            helperText={`Type "${challenge}" to confirm`}
            sx={{ mt: 2, '& input': { fontFamily: MONO, fontSize: '0.8125rem' } }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={blocked}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ==============================|| EVENT PRESENTATION (shared) ||============================== //

// Terminal event-tag accents — used inside the always-dark terminal surfaces,
// so they are deliberately fixed hexes rather than palette tokens.
export const EVENT_TAG_COLORS = {
  error: '#FF6262',
  warn: '#FEBC2E',
  run: '#4DD48A',
  info: '#3BA0FF',
  muted: 'rgba(255,255,255,0.55)',
  faint: 'rgba(255,255,255,0.45)'
} as const;

// Map control-plane event types onto terminal tags + colours. Single source
// for the fleet feed (MyBotsSection) and the bot activity feed
// (BotDetailSection) — these had drifted into two copies.
export const eventMeta = (e: { eventType?: string; level?: string }): { tag: string; color: string } => {
  const t = (e.eventType || '').toLowerCase();
  if (e.level === 'ERROR' || t.includes('fail') || t.includes('crash') || t.includes('error'))
    return { tag: 'ERR', color: EVENT_TAG_COLORS.error };
  if (t.includes('restart')) return { tag: 'RST', color: EVENT_TAG_COLORS.warn };
  if (t.includes('start') || t.includes('running') || t.includes('assign')) return { tag: 'RUN', color: EVENT_TAG_COLORS.run };
  if (t.includes('settings') || t.includes('appl') || t.includes('config')) return { tag: 'CFG', color: EVENT_TAG_COLORS.info };
  if (t.includes('creat')) return { tag: 'NEW', color: EVENT_TAG_COLORS.info };
  if (t.includes('stop') || t.includes('unassign')) return { tag: 'STP', color: EVENT_TAG_COLORS.muted };
  return { tag: 'INFO', color: EVENT_TAG_COLORS.faint };
};

// Tabular money formatting for balance/equity/P&L readouts.
export const fmtMoney = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ==============================|| VPS CONSOLE PRIMITIVES ||============================== //
// EA Connect's product identity: a private-region trading console. Every bot
// is a provisioned instance; slots are capacity cells the user owns. These
// primitives carry that language across Fleet / Deploy / Instance detail.
// Rules: theme-driven colors only (fixed hexes live inside terminalSurface
// contexts); IBM Plex Mono only for ids, hashes, prices, symbols, timestamps and
// versions — never labels or sentences; every animation gates on
// prefers-reduced-motion.

// Mono identity chip for instance ids / hashes: truncated display, full value
// in the tooltip, click-to-copy with a brief confirmation swap.
export const InstanceIdChip = ({ id, prefixLen = 10 }: { id: string; prefixLen?: number }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(copyTimer.current), []);
  const label = id.length > prefixLen + 2 ? `${id.slice(0, prefixLen)}…` : id;
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(id).catch(() => undefined);
    setCopied(true);
    window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(false), 1200);
  };
  return (
    <Tooltip title={copied ? 'Copied' : id}>
      <Box
        onClick={copy}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          height: 20,
          px: 0.75,
          borderRadius: '4px',
          cursor: 'copy',
          fontFamily: MONO,
          fontSize: '0.6875rem',
          letterSpacing: '0.02em',
          color: 'text.secondary',
          bgcolor: alpha(theme.palette.text.primary, dark ? 0.09 : 0.05),
          border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          '&:hover .ea-id-copy': { opacity: 1 }
        }}
      >
        {label}
        <Box
          className="ea-id-copy"
          sx={{
            display: 'flex',
            fontSize: 10,
            opacity: copied ? 1 : 0,
            transition: 'opacity 120ms ease',
            color: copied ? 'success.main' : 'inherit',
            [REDUCED_MOTION]: { transition: 'none' }
          }}
        >
          {copied ? <CheckOutlined /> : <CopyOutlined />}
        </Box>
      </Box>
    </Tooltip>
  );
};

// Status pill wrapping the StatusDot ping core — the instance-state signal for
// row/header contexts where a bare dot is too quiet.
export const StatusChip = ({ status, pulse, ink = 'auto' }: { status: string; pulse?: boolean; ink?: 'auto' | 'onDark' }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const onDark = ink === 'onDark';
  const palKey = STATUS_PALETTE[status];
  const pal = palKey ? theme.palette[palKey] : undefined;
  // base paints the dot/border/wash; text uses the mode-appropriate shade so
  // the 12px label clears contrast on light backgrounds. On the fixed-dark
  // command bands (ink="onDark") the light shade is forced in BOTH modes —
  // pal.dark on #0C1226 is ~2:1, unreadable.
  const base = pal?.main ?? theme.palette.grey[500];
  const lightShade = pal ? pal.light : BAND_INK.mid;
  const text = onDark ? lightShade : pal ? (dark ? pal.light : pal.dark) : dark ? theme.palette.grey[400] : theme.palette.grey[700];
  const dot = onDark ? lightShade : base;
  const label = STATUS_META[status]?.label ?? status;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        display: 'inline-flex',
        height: 22,
        px: 1.25,
        borderRadius: 999,
        border: `1px solid ${alpha(onDark ? lightShade : base, 0.35)}`,
        bgcolor: alpha(base, onDark ? 0.18 : dark ? 0.12 : 0.07)
      }}
    >
      <Box sx={{ position: 'relative', width: 7, height: 7, flexShrink: 0 }}>
        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: dot }} />
        {pulse && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: `1.5px solid ${dot}`,
              animation: 'eaPing 1.8s cubic-bezier(0, 0, 0.2, 1) infinite',
              '@keyframes eaPing': {
                '0%': { transform: 'scale(1)', opacity: 0.7 },
                '100%': { transform: 'scale(2.4)', opacity: 0 }
              },
              [REDUCED_MOTION]: { animation: 'none', opacity: 0 }
            }}
          />
        )}
      </Box>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: text, lineHeight: 1 }}>{label}</Typography>
    </Stack>
  );
};

// Capacity as owned cells — THE upsell primitive. Used cells are solid, cells
// backing errored bots are red, free cells are dashed outlines ("capacity you
// are paying for, idle"), and the terminal ghost cell is a one-click purchase
// surface. Never a banner, never a nag.
export const SlotCapacityBar = ({
  used,
  limit,
  errorCount = 0,
  size = 'md',
  onAddSlot,
  slotPrice,
  ink = 'auto'
}: {
  used: number;
  limit: number;
  errorCount?: number;
  size?: 'sm' | 'md';
  onAddSlot?: () => void;
  // Formatted live price of one slot ("$10") for the add-slot tooltip.
  slotPrice?: string;
  /** 'onDark' pins light inks for fixed-dark surfaces (the command bands),
   *  where theme-tracking greys would vanish in light mode. */
  ink?: 'auto' | 'onDark';
}) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const onDark = ink === 'onDark';
  const labelInk = onDark ? 'rgba(214,224,238,0.45)' : 'text.secondary';
  const countInk = onDark ? 'rgba(214,224,238,0.62)' : 'text.secondary';
  const freeDash = onDark ? 'rgba(214,224,238,0.35)' : alpha(theme.palette.text.primary, dark ? 0.28 : 0.35);
  const h = size === 'md' ? 10 : 8;
  const cells = Array.from({ length: Math.max(limit, 0) }, (_, i) => {
    if (i < used - errorCount) return 'used';
    if (i < used) return 'error';
    return 'free';
  });
  return (
    <Box sx={{ minWidth: { xs: 0, sm: size === 'md' ? 220 : 140 } }}>
      {size === 'md' && (
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: labelInk }}>
            Capacity
          </Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.75rem', color: countInk, ...tnum }}>
            {used} / {limit} slots
          </Typography>
        </Stack>
      )}
      <Stack direction="row" spacing="3px" alignItems="stretch">
        {cells.map((kind, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: h,
              borderRadius: '3px',
              transformOrigin: 'left',
              animation: 'eaCellIn 300ms ease-out backwards',
              animationDelay: `${i * 60}ms`,
              '@keyframes eaCellIn': { from: { transform: 'scaleX(0)' } },
              [REDUCED_MOTION]: { animation: 'none' },
              ...(kind === 'used' && { bgcolor: onDark ? '#00A78E' : 'primary.main' }),
              ...(kind === 'error' && { bgcolor: onDark ? BAND_INK.down : 'error.main' }),
              ...(kind === 'free' && {
                border: `1px dashed ${freeDash}`
              })
            }}
          />
        ))}
        {onAddSlot && (
          <Tooltip title={slotPrice ? `Add a slot — ${slotPrice}/mo` : 'Add a slot'}>
            <Box
              onClick={onAddSlot}
              sx={{
                flex: 1,
                height: h,
                minWidth: 18,
                borderRadius: '3px',
                border: `1px dashed ${onDark ? 'rgba(90,200,188,0.6)' : alpha(theme.palette.primary.main, 0.55)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: onDark ? BAND_INK.teal : 'primary.main',
                fontSize: 9,
                lineHeight: 0,
                '&:hover': { bgcolor: onDark ? 'rgba(90,200,188,0.12)' : alpha(theme.palette.primary.main, 0.08) }
              }}
            >
              <PlusOutlined />
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

// Money is the heartbeat: a compact signed P&L pill. Colors nothing but
// itself — card chrome stays neutral even on a red week.
export const PnlChip = ({
  value,
  currency = '',
  size = 'sm',
  ink = 'auto'
}: {
  value: number | null | undefined;
  currency?: string;
  size?: 'sm' | 'md';
  /** 'onDark' pins band-safe shades — theme success/error mains sit below
   *  AA on the fixed-dark command bands in both modes. */
  ink?: 'auto' | 'onDark';
}) => {
  const theme = useTheme();
  const onDark = ink === 'onDark';
  // The pill both states share; only the tint differs.
  const pill = {
    display: 'inline-flex',
    alignItems: 'center',
    height: size === 'md' ? 26 : 22,
    px: 1,
    borderRadius: '6px',
    fontSize: size === 'md' ? '0.8125rem' : '0.75rem',
    fontWeight: 700
  };
  if (value == null) {
    return (
      <Box
        component="span"
        sx={{
          ...pill,
          bgcolor: onDark ? 'rgba(255,255,255,0.07)' : alpha(theme.palette.text.primary, 0.06),
          border: onDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
          color: onDark ? BAND_INK.mid : 'text.secondary'
        }}
      >
        —
      </Box>
    );
  }
  const up = value >= 0;
  const c = onDark ? (up ? BAND_INK.up : BAND_INK.down) : up ? theme.palette.success.main : theme.palette.error.main;
  return (
    <Box component="span" sx={{ ...pill, bgcolor: alpha(c, onDark ? 0.16 : 0.12), color: c, ...tnum }}>
      {up ? '+' : ''}
      {fmtMoney(value)}
      {currency ? ` ${currency}` : ''}
    </Box>
  );
};

// Background flash when a live value changes — green up, red down. Wraps
// inline; never shifts layout. Turns polling refreshes into visible telemetry.
export const PulseValue = ({ value, children }: { value: number | null | undefined; children: ReactNode }) => {
  const theme = useTheme();
  const prev = useRef<number | null | undefined>(value);
  const [flash, setFlash] = useState<'up' | 'down' | null>(null);
  useEffect(() => {
    if (prev.current != null && value != null && value !== prev.current) {
      setFlash(value > prev.current ? 'up' : 'down');
      const t = window.setTimeout(() => setFlash(null), 800);
      prev.current = value;
      return () => window.clearTimeout(t);
    }
    prev.current = value;
    return undefined;
  }, [value]);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        borderRadius: 1,
        mx: -0.5,
        px: 0.5,
        transition: 'background-color 800ms ease-out',
        bgcolor: flash ? alpha(flash === 'up' ? theme.palette.success.main : theme.palette.error.main, 0.14) : 'transparent',
        [REDUCED_MOTION]: { transition: 'none', bgcolor: 'transparent' }
      }}
    >
      {children}
    </Box>
  );
};

// Key/value row for provenance panels (pre-flight review, instance panel).
// plain: free text (e.g. an EA input's description) in normal case, cut with an ellipsis, value on the right.
export const MetaKV = ({
  label,
  mono,
  last,
  plain,
  children
}: {
  label: string;
  mono?: boolean;
  last?: boolean;
  plain?: boolean;
  children: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: plain ? 'minmax(0, 1fr) auto' : '120px 1fr',
        columnGap: plain ? 2 : 0,
        alignItems: 'baseline',
        py: 1,
        borderBottom: last ? 'none' : `1px solid ${alpha(theme.palette.divider, 0.6)}`
      }}
    >
      <Typography
        title={plain ? label : undefined}
        sx={
          plain
            ? { fontSize: '0.75rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
            : { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }
        }
      >
        {label}
      </Typography>
      <Box
        sx={{
          fontSize: '0.8125rem',
          color: 'text.primary',
          minWidth: 0,
          ...(plain && { textAlign: 'right' }),
          ...(mono && { fontFamily: MONO, ...tnum })
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

// Platform monogram pill — MT4 amber, MT5 teal; the only place platform gets
// a color code.
// The real MetaTrader marks (transparent 50px PNGs shared with the account
// pages) — recognition beats a two-letter code everywhere the platform shows.
const PLATFORM_LOGOS: Record<string, string> = { MT4: mt4Logo, MT5: mt5Logo };

// Bare logo mark for tiles and avatars; falls back to nothing for unknown
// platforms (callers keep their own tile chrome).
export const PlatformMark = ({ platform, size = 22 }: { platform: string; size?: number }) => {
  const p = (platform || 'MT5').toUpperCase();
  const src = PLATFORM_LOGOS[p];
  if (!src) return null;
  return (
    <Box
      component="img"
      src={src}
      alt={p === 'MT4' ? 'MetaTrader 4' : 'MetaTrader 5'}
      sx={{ width: size, height: size, display: 'block', flexShrink: 0 }}
    />
  );
};

export const PlatformBadge = ({ platform }: { platform: string }) => {
  const theme = useTheme();
  const p = (platform || 'MT5').toUpperCase();
  const logo = PLATFORM_LOGOS[p];
  return (
    <ToneBadge tint={p === 'MT4' ? theme.palette.warning.main : theme.palette.primary.main}>
      {logo && <Box component="img" src={logo} alt="" sx={{ width: 12, height: 12, display: 'block' }} />}
      {p}
    </ToneBadge>
  );
};

// Small tinted mono pill for short tags (platform, demo/live).
export const ToneBadge = ({ tint, children }: { tint: string; children: ReactNode }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: 20,
        px: 0.875,
        borderRadius: 999,
        border: `1px solid ${alpha(tint, 0.35)}`,
        bgcolor: alpha(tint, theme.palette.mode === 'dark' ? 0.12 : 0.07),
        fontFamily: MONO,
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        color: tint,
        flexShrink: 0
      }}
    >
      {children}
    </Box>
  );
};

// Region tag tying every page to the datacenter world of LowLatencyBanner.
export const RegionBadge = ({ region = 'LDN', live, ink = 'auto' }: { region?: string; live?: boolean; ink?: 'auto' | 'onDark' }) => {
  const theme = useTheme();
  const onDark = ink === 'onDark';
  const c = onDark ? BAND_INK.teal : theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.625,
        height: 20,
        px: 0.75,
        borderRadius: '5px',
        border: `1px solid ${onDark ? 'rgba(90,200,188,0.35)' : alpha(theme.palette.primary.main, 0.25)}`,
        bgcolor: onDark ? 'rgba(90,200,188,0.1)' : alpha(theme.palette.primary.main, 0.07),
        fontFamily: MONO,
        fontSize: '0.625rem',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: c
      }}
    >
      <Box
        sx={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          bgcolor: c,
          ...(live && {
            animation: 'eaRegionPulse 2s ease-in-out infinite',
            '@keyframes eaRegionPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
            [REDUCED_MOTION]: { animation: 'none' }
          })
        }}
      />
      {region}
    </Box>
  );
};

// Vertical provisioning rail for the staged deploy flow; horizontal below md.
// Clearance below the fixed app bar: the rail sticks here, and jumped-to stages land here.
export const RAIL_STICKY_TOP = 88;

export const DeployStepRail = ({
  steps,
  active,
  done,
  onStepClick,
  stepEnabled
}: {
  steps: string[];
  active: number;
  done: boolean[];
  onStepClick?: (step: number) => void;
  stepEnabled?: boolean[];
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        alignItems: { xs: 'center', md: 'flex-start' },
        justifyContent: { xs: 'center', md: 'flex-start' },
        position: { md: 'sticky' },
        top: { md: RAIL_STICKY_TOP },
        width: { md: 210 },
        mb: { xs: 3, md: 0 },
        flexShrink: 0
      }}
    >
      {steps.map((label, i) => {
        const isDone = done[i];
        const isActive = i === active && !isDone;
        const clickable = !!onStepClick && (stepEnabled?.[i] ?? true);
        return (
          <Box
            key={label}
            sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, alignItems: { xs: 'center', md: 'flex-start' } }}
          >
            {/* Labels hide below md, so the button carries its own name. */}
            <ButtonBase
              disabled={!clickable}
              onClick={() => onStepClick?.(i)}
              aria-label={label}
              aria-current={isActive ? 'step' : undefined}
              sx={{ ...focusRingSx(theme), borderRadius: RADIUS.pill, '&:hover .MuiTypography-root': { color: 'text.primary' } }}
            >
              <Stack direction="row" alignItems="center" spacing={{ xs: 0, md: 1.5 }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    ...(isDone && { bgcolor: 'primary.main', color: theme.palette.primary.contrastText }),
                    ...(isActive && {
                      border: `1.5px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.1)
                    }),
                    ...(!isDone && !isActive && { border: `1.5px solid ${theme.palette.border.main}`, color: 'text.secondary' })
                  }}
                >
                  {isDone ? <CheckOutlined style={{ fontSize: 12 }} /> : i + 1}
                  {isActive && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: -1.5,
                        borderRadius: '50%',
                        border: `1.5px solid ${theme.palette.primary.main}`,
                        animation: 'eaPing 2.4s cubic-bezier(0, 0, 0.2, 1) infinite',
                        '@keyframes eaPing': {
                          '0%': { transform: 'scale(1)', opacity: 0.6 },
                          '100%': { transform: 'scale(1.7)', opacity: 0 }
                        },
                        [REDUCED_MOTION]: { animation: 'none', opacity: 0 }
                      }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    fontSize: '0.8125rem',
                    fontWeight: isActive || isDone ? 600 : 500,
                    color: isActive ? 'text.primary' : 'text.secondary'
                  }}
                >
                  {label}
                </Typography>
              </Stack>
            </ButtonBase>
            {i < steps.length - 1 && (
              <Box
                sx={{
                  bgcolor: isDone ? 'primary.main' : theme.palette.border.main,
                  transition: 'background-color 300ms ease',
                  width: { xs: 28, md: '1.5px' },
                  height: { xs: '1.5px', md: 28 },
                  mx: { xs: 1, md: 0 },
                  ml: { md: '12.25px' },
                  my: { md: 0.5 }
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// Build-log console for extraction / instance launch — real states, no fake
// theatre. Lines carry a glyph kind mapped onto the terminal tag colors.
export type ConsoleLineKind = 'step' | 'ok' | 'err' | 'dim';
export type ConsoleLogLine = { kind: ConsoleLineKind; text: string };

const CONSOLE_GLYPH: Record<ConsoleLineKind, { glyph: string; color: string }> = {
  step: { glyph: '→', color: EVENT_TAG_COLORS.info },
  ok: { glyph: '✓', color: EVENT_TAG_COLORS.run },
  err: { glyph: '✗', color: EVENT_TAG_COLORS.error },
  dim: { glyph: '·', color: EVENT_TAG_COLORS.faint }
};

export const ExtractionConsole = ({
  title,
  lines,
  state,
  minHeight = 150,
  action
}: {
  title: ReactNode;
  lines: ConsoleLogLine[];
  state: 'running' | 'done' | 'failed';
  minHeight?: number;
  action?: ReactNode;
}) => {
  const theme = useTheme();
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());
  useEffect(() => {
    if (state !== 'running') return undefined;
    // Each (re-)entry into 'running' is a fresh run — a retry after a failed
    // extraction must count from 00:00, not from the console's mount time.
    startRef.current = Date.now();
    setElapsed(0);
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [state]);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  return (
    <Box sx={{ ...terminalShell(theme), position: 'relative' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={terminalHeaderSx}>
        <Box sx={{ fontFamily: MONO, fontSize: '0.75rem', color: '#fff', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          {title}
        </Box>
        {action}
      </Stack>
      <Box sx={{ px: 2.5, py: 1.5, minHeight, maxHeight: 260, overflowY: 'auto' }}>
        {lines.map((l, i) => {
          const g = CONSOLE_GLYPH[l.kind];
          return (
            <Typography
              key={i}
              sx={{ fontFamily: MONO, fontSize: '0.71875rem', lineHeight: 1.9, color: 'rgba(255,255,255,0.82)', ...reveal(Math.min(i, 8)) }}
            >
              <Box component="span" sx={{ color: g.color, mr: 1 }}>
                {g.glyph}
              </Box>
              {l.text}
            </Typography>
          );
        })}
        {state === 'running' && (
          <Typography sx={{ fontFamily: MONO, fontSize: '0.71875rem', lineHeight: 1.9, color: 'rgba(255,255,255,0.45)', ...tnum }}>
            elapsed {mm}:{ss}
          </Typography>
        )}
      </Box>
      {/* Footer strip: indeterminate sweep while running; solid verdict after. */}
      <Box sx={{ position: 'relative', height: 2, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.06)' }}>
        {state === 'running' ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              width: '40%',
              bgcolor: theme.palette.primary.main,
              animation: 'eaSweep 1.6s ease-in-out infinite',
              '@keyframes eaSweep': { from: { transform: 'translateX(-100%)' }, to: { transform: 'translateX(350%)' } },
              [REDUCED_MOTION]: { animation: 'none', width: '100%', transform: 'none' }
            }}
          />
        ) : (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: state === 'done' ? EVENT_TAG_COLORS.run : EVENT_TAG_COLORS.error }} />
        )}
      </Box>
    </Box>
  );
};

// The table-footer row occupying the exact footprint the next bot would fill.
// 'free' invites a deploy; 'full' offers a slot inline at its live price. Hitting the
// capacity wall lands on furniture, not an error.
export const GhostSlotRow = ({
  variant,
  freeCount = 0,
  slotPrice,
  onDeploy,
  onAddSlot
}: {
  variant: 'free' | 'full';
  freeCount?: number;
  // Formatted live price of one slot ("$10"), from useEaHostingSlotPrice.
  slotPrice: string;
  onDeploy: () => void;
  onAddSlot: () => void;
}) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={1.25}
      sx={{
        height: 56,
        mx: 2,
        my: 1.5,
        borderRadius: '8px',
        border: `1.5px dashed ${alpha(theme.palette.text.primary, 0.18)}`,
        transition: 'border-color 180ms ease, background-color 180ms ease',
        '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.5), bgcolor: alpha(theme.palette.primary.main, 0.03) }
      }}
    >
      {variant === 'free' ? (
        <>
          <Box sx={{ display: 'flex', color: 'text.secondary', fontSize: 13 }}>
            <PlusOutlined />
          </Box>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
            {freeCount} slot{freeCount === 1 ? '' : 's'} free — deploy another EA
          </Typography>
          <Button size="small" variant="text" onClick={onDeploy}>
            Deploy
          </Button>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', color: 'warning.main', fontSize: 13 }}>
            <ThunderboltOutlined />
          </Box>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>Fleet at capacity</Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>· add a slot for</Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', fontWeight: 600, color: 'primary.main', ...tnum }}>
            {slotPrice}/mo
          </Typography>
          <Button size="small" variant="outlined" onClick={onAddSlot}>
            Add slot
          </Button>
        </>
      )}
    </Stack>
  );
};

// Live-ticking clock for uptime readouts: one interval per hook consumer,
// still ticks under reduced motion (it is content, not decoration).
export const useNowSec = (enabled = true) => {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [enabled]);
  return now;
};

// Self-contained ticking clock. The hook below re-renders its owner once a
// second, so the owner must be this tiny component and never a page.
export const UptimeClock = memo(({ startedAtSec, live, sx }: { startedAtSec: number; live: boolean; sx?: any }) => {
  const nowSec = useNowSec(live);
  return (
    <Typography component="span" sx={sx}>
      {fmtUptimeClock(startedAtSec, nowSec)}
    </Typography>
  );
});
UptimeClock.displayName = 'UptimeClock';

// `14d 06:22:41` — the visceral uptime format; '—' when never started.
export const fmtUptimeClock = (startedAtSec: number, nowSec: number) => {
  if (!startedAtSec) return '—';
  let secs = Math.max(0, nowSec - startedAtSec);
  const days = Math.floor(secs / 86400);
  secs -= days * 86400;
  const h = String(Math.floor(secs / 3600)).padStart(2, '0');
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
  const s = String(secs % 60).padStart(2, '0');
  return days > 0 ? `${days}d ${h}:${m}:${s}` : `${h}:${m}:${s}`;
};
